import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
// Copyright 1999-2020. Plesk International GmbH. All rights reserved.

/* eslint-disable react/no-deprecated */
import React, { cloneElement, isValidElement, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Icon from '../Icon';
import Spinner from '../Spinner';
import Tooltip from '../Tooltip';
var oldIconNames = {
  "customize-order": "arrows-four-directions",
  download: "arrow-down-tray",
  edit: "pencil",
  folder: "folder-closed",
  "globe-alt": "globe",
  "import": "arrow-down-in",
  invisible: "eye-closed",
  link: "chain",
  lock: "lock-closed",
  "ok-circle": "check-mark-circle",
  "question-circle": "question-mark-circle",
  "security-scan": "shield",
  settings: "sliders",
  suspend: "sleep",
  unlock: "lock-open",
  upload: "arrow-up-tray",
  visible: "eye"
};
import ResponsiveContext from '../ResponsiveContext';
import OnDarkContext from '../OnDarkContext';

var renderIcon = function renderIcon(_ref) {
  var baseClassName = _ref.baseClassName,
      icon = _ref.icon,
      size = _ref.size,
      empty = _ref.empty;
  return Icon.create(oldIconNames[icon] || icon, {
    className: "".concat(baseClassName, "__icon"),
    size: size === 'lg' || empty ? '16' : '12'
  });
};

var renderCaret = function renderCaret(_ref2) {
  var baseClassName = _ref2.baseClassName,
      size = _ref2.size,
      caret = _ref2.caret;
  var caretElement;

  if (typeof caret === 'boolean' && caret) {
    caretElement = React.createElement(Icon, {
      name: "chevron-down",
      size: size === 'lg' ? '16' : '12'
    });
  } else {
    caretElement = caret;
  }

  if (isValidElement(caretElement)) {
    caretElement = cloneElement(caretElement, {
      className: classNames("".concat(baseClassName, "__caret"), caretElement.props.className)
    });
  }

  return caretElement;
};
/**
 * `Button` component is used for executing actions, changing the application state, opening forms or dialog windows, and submitting data.
 * As a rule, you should not use buttons for navigation between individual pages (use `Link` instead).
 * @since 0.0.37
 */


var Button = function Button(_ref3) {
  var _classNames;

  var baseClassName = _ref3.baseClassName,
      className = _ref3.className,
      Tag = _ref3.component,
      size = _ref3.size,
      intent = _ref3.intent,
      ghost = _ref3.ghost,
      selectedProp = _ref3.selected,
      onToggle = _ref3.onToggle,
      state = _ref3.state,
      icon = _ref3.icon,
      caret = _ref3.caret,
      fill = _ref3.fill,
      tooltip = _ref3.tooltip,
      children = _ref3.children,
      arrow = _ref3.arrow,
      disabled = _ref3.disabled,
      props = _objectWithoutProperties(_ref3, ["baseClassName", "className", "component", "size", "intent", "ghost", "selected", "onToggle", "state", "icon", "caret", "fill", "tooltip", "children", "arrow", "disabled"]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      selectedState = _useState2[0],
      setSelectedState = _useState2[1];

  var handleToggle = function handleToggle() {
    setSelectedState(!selectedState);
    return !selectedState;
  };

  var isCollapsed = useContext(ResponsiveContext);
  var componentProps = {};

  if (Tag === 'button') {
    componentProps.type = 'button';
  } else if (Tag === 'a') {
    componentProps.role = 'button';
    componentProps.tabIndex = 0;

    if (disabled) {
      props.href = undefined;
    }
  }

  if (props.onClick && disabled) {
    props.onClick = function (e) {
      e.preventDefault();
    };
  }

  var childrenElement = children;
  var tooltipElement = tooltip;

  if (isCollapsed && icon && children) {
    childrenElement = null;

    if (!tooltip) {
      tooltipElement = children;
    }
  }

  if (!props.onClick && onToggle) {
    props.onClick = function (e) {
      e.preventDefault();
      onToggle(handleToggle());
    };
  }

  var selected = selectedProp === undefined ? selectedState : selectedProp;

  if (selectedProp !== undefined || onToggle) {
    componentProps['aria-pressed'] = String(selected);
  }

  var isArrowButton = arrow === 'forward' || arrow === 'backward';
  var hasAriaDisabled = Tag === 'button' && disabled && tooltip;
  var button = React.createElement(Tag, _extends({
    className: classNames(baseClassName, (_classNames = {}, _defineProperty(_classNames, "".concat(baseClassName, "--").concat(size), size), _defineProperty(_classNames, "".concat(baseClassName, "--").concat(intent), intent && !ghost), _defineProperty(_classNames, "".concat(baseClassName, "--ghost"), ghost), _defineProperty(_classNames, "".concat(baseClassName, "--").concat(state), state), _defineProperty(_classNames, "".concat(baseClassName, "--selected"), selected), _defineProperty(_classNames, "".concat(baseClassName, "--disabled"), disabled), _defineProperty(_classNames, "".concat(baseClassName, "--empty"), !childrenElement), _defineProperty(_classNames, "".concat(baseClassName, "--caret"), caret), _defineProperty(_classNames, "".concat(baseClassName, "--fill"), fill), _defineProperty(_classNames, "".concat(baseClassName, "--arrow ").concat(baseClassName, "--arrow-").concat(arrow), isArrowButton && !ghost), _defineProperty(_classNames, "".concat(baseClassName, "--on-dark"), useContext(OnDarkContext)), _classNames), className),
    disabled: hasAriaDisabled ? undefined : disabled,
    "aria-disabled": hasAriaDisabled ? 'true' : undefined
  }, componentProps, props), React.createElement("span", {
    className: "".concat(baseClassName, "__inner")
  }, state === 'loading' ? React.createElement(Spinner, {
    className: "".concat(baseClassName, "__spinner")
  }) : renderIcon({
    baseClassName: baseClassName,
    icon: icon,
    size: size,
    empty: !childrenElement
  }), React.createElement("span", null, childrenElement), renderCaret({
    baseClassName: baseClassName,
    size: size,
    caret: caret
  })), isArrowButton && !ghost && React.createElement("span", {
    className: "".concat(baseClassName, "__arrow")
  }));

  if (tooltipElement) {
    button = React.createElement(Tooltip, {
      title: tooltipElement
    }, button);
  }

  return button;
};

Button.propTypes = {
  /**
   * Button size.
   * @since 2.5.0
   */
  size: PropTypes.oneOf(['md', 'lg']),

  /**
   * Visual intent color to apply to component.
   * @since 0.0.42
   */
  intent: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'danger', 'info']),

  /**
   * Display button with transparent background.
   * @since 2.1.0
   */
  ghost: PropTypes.bool,

  /**
   * Arrow button.
   * @since 2.6.0
   */
  arrow: PropTypes.oneOf(['forward', 'backward']),

  /**
   * Selected state of toggle button. Used in controlled component mode.
   * @since 0.0.68
   */
  selected: PropTypes.bool,

  /**
   * Toggle button callback. Used in uncontrolled component mode.
   * @since 0.0.68
   */
  onToggle: PropTypes.func,

  /**
   * State of the button
   * @since 0.0.42
   */
  state: PropTypes.oneOf(['active', 'loading', 'hovered', 'focused']),

  /**
   * Is button disabled?
   * @since 0.0.42
   */
  disabled: PropTypes.bool,

  /**
   * Tooltip for component
   * @since 0.0.42
   */
  tooltip: PropTypes.node,

  /**
   * Name of icon or [Icon](#!/Icon) component
   * @since 0.0.42
   */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.element]),

  /**
   * Show caret
   * @since 0.0.42
   */
  caret: PropTypes.oneOfType([PropTypes.bool, PropTypes.element]),

  /**
   * Dynamically expand the button to fill out the whole available width of its parent container.
   * @since 1.8.0
   */
  fill: PropTypes.bool,

  /**
   * Content of the button
   * @since 0.0.37
   */
  children: PropTypes.node,

  /**
   * Component to render as the root element. Useful when rendering a `Button` as `<a>` or `<Link>`.
   * @since 0.0.42
   */
  component: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string
};
Button.defaultProps = {
  size: undefined,
  intent: undefined,
  ghost: false,
  selected: undefined,
  onToggle: undefined,
  state: undefined,
  disabled: false,
  tooltip: undefined,
  icon: undefined,
  caret: undefined,
  fill: false,
  children: undefined,
  component: 'button',
  className: undefined,
  baseClassName: "".concat(CLS_PREFIX, "button"),
  arrow: undefined
};
export default Button;